import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import { useArticle, logArticleView } from '../articlesApi';
import { useSEO } from '../hooks/useSEO';
import NewsletterSignup from '../components/NewsletterSignup';

const Block = ({ block }) => {
  if (block.type === 'p') {
    return <p className="text-slate-700 leading-[1.75] text-[17px] mb-6">{block.text}</p>;
  }
  if (block.type === 'h2') {
    return <h2 className="text-2xl md:text-3xl font-medium text-slate-900 tracking-tight mt-12 mb-5 leading-tight">{block.text}</h2>;
  }
  if (block.type === 'h3') {
    return <h3 className="text-xl font-medium text-slate-900 tracking-tight mt-8 mb-3">{block.text}</h3>;
  }
  if (block.type === 'quote') {
    return (
      <blockquote className="my-8 pl-6 border-l-4 border-blue-600 text-xl md:text-2xl text-slate-900 font-medium italic leading-snug">
        &ldquo;{block.text}&rdquo;
      </blockquote>
    );
  }
  if (block.type === 'list') {
    return (
      <ul className="my-6 space-y-3">
        {block.items.map((it, i) => (
          <li key={i} className="flex gap-3 text-slate-700 leading-[1.7] text-[16.5px]">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  return null;
};

const V2ResourceArticle = () => {
  const { slug } = useParams();
  const { article, loading, error } = useArticle(slug);

  // Fire the view-count ping once per mount, only after the article
  // successfully loads. Guarded by `article?.slug` so failed loads don't
  // increment counters and we don't double-count during React StrictMode
  // dev renders (the effect still runs twice, but the ping is idempotent
  // enough, a rare double-tick is acceptable for a soft analytics signal).
  useEffect(() => {
    if (article?.slug) logArticleView(article.slug);
  }, [article?.slug]);

  useSEO({
    title: article?.seo?.title || 'Insight, Keystone Strategic Consultants',
    description: article?.seo?.description || 'Practitioner-led insight from Keystone Strategic Consultants.',
    path: `/insights/${slug || ''}`,
  });

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-500 text-sm">
        Loading article…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
        <p className="text-slate-600 mb-4">We had trouble loading this article just now.</p>
        <button
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-2 border border-slate-300 hover:border-blue-500 hover:text-blue-700 text-slate-700 rounded-full px-5 py-2 text-sm transition-colors"
          data-testid="v2-article-retry-btn"
        >
          Try again
        </button>
        <Link to="/insights" className="mt-4 text-sm text-blue-600 hover:text-blue-700">
          Or return to all insights
        </Link>
      </div>
    );
  }
  if (!article) return <Navigate to="/insights" replace />;

  return (
    <div className="bg-white text-slate-900" data-testid={`v2-resource-article-${slug}`}>
      {/* Article schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.seo.description,
            about: article.kicker,
            author: {
              '@type': 'Organization',
              name: 'Keystone Strategic Consultants',
              url: 'https://keystoneconsulting.uk',
            },
            publisher: {
              '@type': 'Organization',
              name: 'Keystone Strategic Consultants',
              url: 'https://keystoneconsulting.uk',
            },
            mainEntityOfPage: `https://keystoneconsulting.uk/insights/${slug}`,
          }),
        }}
      />

      {/* Hero */}
      <section className="pt-14 md:pt-20 pb-12 border-b border-slate-200 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/insights"
            className="inline-flex items-center text-sm text-slate-600 hover:text-blue-700 transition-colors mb-8"
            data-testid="v2-resource-article-back"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            All insights
          </Link>
          <p className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">
            {article.kicker}
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight text-slate-900 leading-[1.1] mb-6">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> {article.readMinutes} min read
            </span>
            <span>·</span>
            <span>Keystone Strategic Consultants</span>
          </div>
        </div>
      </section>

      {/* Body */}
      <article className="py-14 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {article.blocks.map((b, i) => (
            <Block key={i} block={b} />
          ))}

          {/* Inline newsletter capture at the end of the body, highest-intent
              moment: reader has just finished the piece. */}
          <div className="mt-14" data-testid="v2-article-newsletter">
            <NewsletterSignup source={`article:${slug}`} variant="article" />
          </div>
        </div>
      </article>

      {/* CTA */}
      <section className="py-14 border-t border-slate-200 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-slate-900 mb-4">
            Want to apply this to your programme?
          </h2>
          <p className="text-slate-600 mb-8 max-w-xl mx-auto">
            A short call with the Keystone team, practical, not a pitch.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center bg-slate-900 text-white px-7 py-3.5 rounded-md font-medium hover:bg-slate-800 transition-all group"
            data-testid="v2-resource-article-cta"
          >
            Book a call
            <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default V2ResourceArticle;
